import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'

const TOOLTIP_CLASS =
  'pointer-events-none fixed z-[100] max-w-sm -translate-x-1/2 -translate-y-full rounded-lg border border-transparent bg-slate-700 px-3 py-2 text-left text-xs font-medium text-white shadow-sm select-none dark:border-0 dark:bg-slate-950'

export interface PortaledTooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  className?: string
}

/** Portaled so sticky page/toolbar chrome cannot cover the tooltip. */
export const PortaledTooltip: React.FC<PortaledTooltipProps> = ({ content, children, className }) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const show = (): void => {
    const rect = targetRef.current?.getBoundingClientRect()
    if (rect == null) return
    // Leave room for the downward arrow between tooltip and trigger.
    setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 })
    setOpen(true)
  }

  return (
    <>
      <div
        ref={targetRef}
        className={classnames('max-w-full', className)}
        onMouseEnter={show}
        onMouseOver={show}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </div>
      {open &&
        createPortal(
          // Keep `fixed` only — do not add `relative` (it overrides `fixed` in Tailwind).
          <div role="tooltip" className={TOOLTIP_CLASS} style={{ top: pos.top, left: pos.left }}>
            <span
              aria-hidden
              className="absolute bottom-0 left-1/2 translate-y-full -translate-x-1/2 border-[5px] border-transparent border-t-slate-700 dark:border-t-slate-950"
            />
            {content}
          </div>,
          document.body
        )}
    </>
  )
}

export default PortaledTooltip
